import React, { useState, useEffect } from 'react';
import "./NewQuery.css";
import { getDatabase, ref, push, get, set } from 'firebase/database';
import { ref as dbRef, onValue } from "firebase/database";
import { FaInfoCircle, FaPaperPlane, FaClock, FaCheckCircle, FaTimesCircle, FaPhone } from 'react-icons/fa';
import { Link } from 'react-router-dom';
// Import Account Context
import { useCurrentUid } from '../hooks/useCurrentUid'; 
import { useAccount } from '../context/AccountContext';
import { MdInfo } from 'react-icons/md';

const gifUrl = "/Images/New notification.gif";

// Categories configuration
const CATEGORIES = {
  CEMENT: 'Cement',
  STEEL: 'Steel',
  MACHINERY: 'Machinery',
  FINANCE: 'Finance'
};

// Field configurations
const categoryFields = {
  [CATEGORIES.CEMENT]: {
    options: {
      brand: [
        { value: 'Ultratech', label: 'Ultratech' },
        { value: 'Shree', label: 'Shree' },
        { value: 'JSW', label: 'JSW' },
        { value: 'ACC', label: 'ACC' },
        { value: 'JKSuper', label: 'JK Super' },
        { value: 'Ambuja', label: 'Ambuja' },
        { value: 'All', label: 'All Brands' }
      ],
      grade: [
        { value: 'OPC43', label: 'OPC43' },
        { value: 'OPC53', label: 'OPC53' },
        { value: 'PSC', label: 'PSC' },
        { value: 'PPC', label: 'PPC' }
      ]
    }
  },
  [CATEGORIES.STEEL]: {
    options: {
      steelType: [
        { value: 'TMT Bars', label: 'TMT Bars (Thermo Mechanically Treated)' },
        { value: 'Mild Steel', label: 'Mild Steel (MS Rods / Angles)' },
        { value: 'Structural Steel', label: 'Structural Steel (Beams, Channels, Angles)' }
      ]
    }
  },
  [CATEGORIES.MACHINERY]: {
    options: {
      machineryType: [
        { value: 'JCB', label: 'JCB' },
        { value: 'Tractor', label: 'Tractor' },
        { value: 'Jack', label: 'Jack' },
        { value: 'Crane', label: 'Crane' }
      ]
    }
  },
  [CATEGORIES.FINANCE]: {
    options: {
      financeType: [
        { value: 'Personal Loan', label: 'Personal Loan' },
        { value: 'Home Loan', label: 'Home Loan' },
        { value: 'Invoice Financing', label: 'Invoice Financing' },
        { value: 'Equipment Financing', label: 'Equipment Financing' },
        { value: 'Business Loan for Women', label: 'Business Loan for Women' },
        { value: 'Term Loan', label: 'Term Loan' }
      ]
    }
  }
};

// Helper: Indian Timestamp
const getIndianTimestamp = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(now.getTime() + istOffset);
  return istTime.toISOString().replace("T", " ").replace("Z", "");
};

// Send Notification (unchanged)
const sendNotification = async (userId, queryId, category, brand, companyName) => {
  const db = getDatabase();
  const notificationRef = ref(db, `supplier_notifications/New Query/${userId}/${queryId}`);

  const newNotification = {
    category,
    brand,
    title: "New Query",
    message: `A new ${category} query has been sent by ${companyName}. Please review the query details.`,
    gifUrl,
    timestamp: getIndianTimestamp(),
    seen: false,
  };

  try {
    await set(notificationRef, newNotification);
  } catch (error) {
    console.error("Error sending notification:", error);
  }
};

const getStatusIcon = (status) => {
  const icons = {
    "Pending": <FaClock className="status-icon pending" />,
    "In Progress": <FaPaperPlane className="status-icon in-progress" />,
    "Resolved": <FaCheckCircle className="status-icon resolved" />,
    "default": <FaTimesCircle className="status-icon error" />
  };
  return icons[status] || icons.default;
};

const NewQuery = ({ latestQuery }) => {
  const currentUid = useCurrentUid();           // Selected account UID
  const { currentAccount, loading: accountLoading } = useAccount(); // For company name

  const [category, setCategory] = useState(CATEGORIES.CEMENT);
  const [formData, setFormData] = useState({
    category: '',
    brand: '',
    grade: '',
    steelType: '',
    machineryType: '',
    financeType: '',
    requiredQuantity: '',
    requirementAmount: '',
    contactNumber: '',
    query: '',
    siteAddress: '',
    timestamp: getIndianTimestamp(),
  });

  const [alertState, setAlertState] = useState({ message: '', variant: '', show: false });
  const [success, setSuccess] = useState(false);
  const [queries, setQueries] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState("");

  // Offline & Connection Check
  useEffect(() => {
    if (!navigator.onLine) {
      setSessionError("You are offline. Please check your internet connection.");
      setLoading(false);
      return;
    }

    const connectedRef = dbRef(getDatabase(), ".info/connected");
    const unsubscribe = onValue(connectedRef, (snap) => {
      if (snap.val() === false) {
        setSessionError("No internet connection.");
      } else {
        setSessionError("");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load latestQuery when provided
  useEffect(() => {
    if (latestQuery) {
      setFormData(latestQuery);
      if (latestQuery.category) {
        setCategory(latestQuery.category);
      }
    }
  }, [latestQuery]);

  // Listen to queries for the CURRENTLY SELECTED account
  useEffect(() => {
    if (!currentUid) return;

    const db = getDatabase();
    const userQueriesRef = ref(db, `queries/${currentUid}`);

    const unsubscribe = onValue(userQueriesRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const queryList = Object.keys(data).map(id => ({
          id,
          ...data[id]
        }));
        setQueries(queryList);
      } else {
        setQueries([]);
      }
    }, (error) => {
      console.error("Error loading queries:", error);
      setAlertState({
        message: 'Failed to load queries.',
        variant: 'danger',
        show: true
      });
    });

    return () => unsubscribe();
  }, [currentUid]); // Re-run when account switches

  const handleCategoryChange = (selectedCategory) => {
    setCategory(selectedCategory);
    setFormData(prev => ({
      ...prev,
      category: selectedCategory,
      brand: '',
      grade: '',
      steelType: '',
      machineryType: '',
      financeType: '',
      requiredQuantity: '',
      requirementAmount: ''
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    const validations = {
      siteAddress: () => value.length > 200 ? "Max 200 chars" : value.length < 5 && value.length > 0 ? "Min 5 chars" : "",
      query: () => value.length > 200 ? "Max 200 chars" : value.length < 10 && value.length > 0 ? "Min 10 chars" : "",
      contactNumber: () => !/^\d{10,15}$/.test(value) && value.length > 0 ? "10-15 digits only" : ""
    };

    if (validations[name]) {
      newErrors[name] = validations[name]();
    }

    setFormData({ ...formData, [name]: value });
    setErrors(newErrors);
  };

  const validateForm = () => {
    let formErrors = {};
    if (!formData.contactNumber || formData.contactNumber.length < 10) formErrors.contactNumber = "Valid number required";
    if (!formData.siteAddress || formData.siteAddress.length < 5) formErrors.siteAddress = "Site address required";
    if (!formData.query || formData.query.length < 10) formErrors.query = "Query too short";

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUid || !currentAccount) {
      setAlertState({ message: 'No account selected.', variant: 'danger', show: true });
      return;
    }

    if (!validateForm()) return;

    try {
      const db = getDatabase();
      const userId = currentUid;
      const companyName = currentAccount.company_name || 'User';

      let queryData = {
        userId,
        companyName,
        submittedAt: getIndianTimestamp(),
        timestamp: getIndianTimestamp(),
        contactNumber: formData.contactNumber,
        siteAddress: formData.siteAddress,
        query: formData.query,
      };

      switch (category) {
        case CATEGORIES.CEMENT:
          queryData.category = CATEGORIES.CEMENT;
          queryData.brand = formData.brand;
          queryData.grade = formData.grade;
          break;
        case CATEGORIES.STEEL:
          queryData.category = CATEGORIES.STEEL;
          queryData.steelType = formData.steelType;
          queryData.requiredQuantity = formData.requiredQuantity;
          break;
        case CATEGORIES.MACHINERY:
          queryData.category = CATEGORIES.MACHINERY;
          queryData.machineryType = formData.machineryType;
          break;
        case CATEGORIES.FINANCE:
          queryData.category = CATEGORIES.FINANCE;
          queryData.financeType = formData.financeType;
          queryData.requirementAmount = formData.requirementAmount;
          break;
      }

      const userQueriesRef = ref(db, `queries/${userId}`);
      const newQueryRef = await push(userQueriesRef, queryData);

      // Send notification (works perfectly)
      await sendNotification(
        userId,
        newQueryRef.key,
        category,
        queryData.brand || queryData.steelType || queryData.machineryType || queryData.financeType || '',
        companyName
      );

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);

      // Reset form
      setFormData({
        category: '', brand: '', grade: '', steelType: '', machineryType: '',
        financeType: '', requiredQuantity: '', requirementAmount: '',
        contactNumber: '', query: '', siteAddress: '', timestamp: getIndianTimestamp()
      });
      setCategory(CATEGORIES.CEMENT);

    } catch (error) {
      console.error("Submit error:", error);
      setAlertState({ message: 'Failed to submit query.', variant: 'danger', show: true });
    }
  };

  // Loading states
  if (loading || accountLoading) {
    return <div className="loading-container"><div className="loader"></div></div>;
  }

  if (sessionError) {
    return <div className="error">{sessionError}</div>;
  }

  if (!currentUid) {
    return <div className="error">No account selected. Please go back and select a firm.</div>;
  }

  // Render helpers (unchanged)
  const renderSelect = (name, options, label, required = true) => (
    <div className="query-group">
      <label className='query-label'>{label}</label>
      <select name={name} value={formData[name]} onChange={handleChange} required={required} className='select-input'>
        <option value=''>Select {label} ▼</option>
        {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );

  const renderCategoryFields = () => {
    switch (category) {
      case CATEGORIES.CEMENT:
        return (
          <>
            {renderSelect('brand', categoryFields[CATEGORIES.CEMENT].options.brand, 'Brand')}
            {renderSelect('grade', categoryFields[CATEGORIES.CEMENT].options.grade, 'Grade')}
          </>
        );
      case CATEGORIES.STEEL:
        return (
          <>
            {renderSelect('steelType', categoryFields[CATEGORIES.STEEL].options.steelType, 'Steel Type')}
            <div className="query-group">
              <label className='query-label'>Required Quantity</label>
              <input type="text" name="requiredQuantity" value={formData.requiredQuantity} onChange={handleChange} placeholder="e.g. 50 tons" className="custom-input" required />
            </div>
          </>
        );
      case CATEGORIES.MACHINERY:
        return renderSelect('machineryType', categoryFields[CATEGORIES.MACHINERY].options.machineryType, 'Machinery Type');
      case CATEGORIES.FINANCE:
        return (
          <>
            {renderSelect('financeType', categoryFields[CATEGORIES.FINANCE].options.financeType, 'Finance Type')}
            <div className="query-group">
              <label className='query-label'>Requirement Amount</label>
              <input type="text" name="requirementAmount" value={formData.requirementAmount} onChange={handleChange} placeholder="e.g. ₹50 Lakhs" className="custom-input" required />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const renderQueryCard = (q) => {
    let status = "Pending";
    if (q.supplierReply?.responseMessage) status = "Resolved";
    else if (q.supplierReply?.seen) status = "In Progress";

    const qCategory = q.category || CATEGORIES.CEMENT;

    return (
      <div key={q.id} className="query-card">
        <div className="query-header">
          <span className="query-status">{getStatusIcon(status)} {status}</span>
          <span className="query-time">{q.submittedAt || q.timestamp}</span>
        </div>
        <div className="query-category">{qCategory}</div>

        {(qCategory === CATEGORIES.CEMENT || !qCategory) && (
          <>
            <p><strong>Brand:</strong> {q.brand}</p>
            <p><strong>Grade:</strong> {q.grade}</p>
          </>
        )}
        {qCategory === CATEGORIES.STEEL && (
          <>
            <p><strong>Steel Type:</strong> {q.steelType}</p>
            <p><strong>Required Quantity:</strong> {q.requiredQuantity}</p>
          </>
        )}
        {qCategory === CATEGORIES.MACHINERY && <p><strong>Machinery Type:</strong> {q.machineryType}</p>}
        {qCategory === CATEGORIES.FINANCE && (
          <>
            <p><strong>Finance Type:</strong> {q.financeType}</p>
            <p><strong>Amount:</strong> {q.requirementAmount}</p>
          </>
        )}

        <p><strong>Contact:</strong> {q.contactNumber || 'Not provided'}</p>
        <p><strong>Site Address:</strong> {q.siteAddress}</p>
        <p><strong>Query:</strong> {q.query}</p>

        {q.supplierReply?.responseMessage ? (
          <p><strong>Supplier Response:</strong> <span className="supplier-response">{q.supplierReply.responseMessage}</span></p>
        ) : (
          <p className="no-response">No response yet from suppliers.</p>
        )}
      </div>
    );
  };

  return (
    <div className="new-query-container">
      <div className="query-form-container">
        <h2>Submit a New Query <MdInfo className='icon-i' onClick={() => alert("Kindly put your query to get the best rates for your sites. Please ensure the site address includes the town, district, and state for accurate responses.")} /></h2>

        <form onSubmit={handleSubmit}>
          <div className="category-toggle">
            {Object.values(CATEGORIES).map(cat => (
              <button key={cat} type="button" className={`toggle-btn ${category === cat ? 'active' : ''}`} onClick={() => handleCategoryChange(cat)}>
                {cat}
              </button>
            ))}
          </div>

          {category && renderCategoryFields()}

          <div className="query-group">
            <label className='query-label'>Contact Number</label>
            <div className="input-with-icon">
              <FaPhone className="input-icon" />
              <input type="tel" name="contactNumber" value={formData.contactNumber} onChange={handleChange} placeholder="Enter your contact number" className="custom-input with-icon" required />
            </div>
            {errors.contactNumber && <p className="error-message">{errors.contactNumber}</p>}
          </div>

          <div className="query-group">
            <label className='query-label'>Site Address</label>
            <input type="text" name="siteAddress" value={formData.siteAddress} onChange={handleChange} required maxLength={200} className="custom-input" placeholder="Enter site address (town, district, state)" />
            {errors.siteAddress && <p className="error-message">{errors.siteAddress}</p>}
            <p className="char-count">{formData.siteAddress.replace(/\s/g, "").length} / 200</p>
          </div>

          <div className="query-group q1-Query-group">
            <label className='query-label'>Query</label>
            <textarea name="query" value={formData.query} onChange={handleChange} required minLength={10} maxLength={200} placeholder="Enter your query (10-200 characters)" rows={4} className="custom-textarea"></textarea>
            {errors.query && <p className="error-message">{errors.query}</p>}
            <p className="char-count">{formData.query.replace(/\s/g, "").length} / 200</p>
          </div>

          <button type="submit" className="custom-submit-btn" disabled={!category}>
            Submit Query
          </button>
          {success && <div className="success-Message text-white">Query submitted successfully!</div>}

          <div className="contact-link">
            <p>Need more help? <Link to="/dashboard/contact-us">Contact Us</Link></p>
          </div>
        </form>
      </div>

      <div className="query-responses-container">
        <h2>Query Responses</h2>
        {queries.length === 0 ? (
          <p className="no-queries">No queries submitted yet.</p>
        ) : (
          [...queries]
            .sort((a, b) => {
              const getPriority = (q) => q.supplierReply?.responseMessage ? 1 : q.supplierReply?.seen ? 2 : 3;
              const priA = getPriority(a), priB = getPriority(b);
              if (priA !== priB) return priA - priB;
              return new Date(b.submittedAt || b.timestamp) - new Date(a.submittedAt || a.timestamp);
            })
            .map(renderQueryCard)
        )}
      </div>
    </div>
  );
};

export default NewQuery;